import * as React from "react"
import cx from "classnames"
import { Link } from "gatsby"
import { Menu, Popper } from "@mui/base"
import InferLogo from "../images/svg/logo.svg"
import BurgerIcon from "../images/svg/burger.svg"
import CloseIcon from "../images/svg/thin-close.svg"
import ChevronIcon from "../images/svg/chevron.svg"

const platformLinks = [
  [
    { title: "How it Works", link: "/how-it-works" },
  ],
]
const solutionsLinks = [
  [
    { title: "By Role" },
    { title: "RevOps", link: "/rev-ops" },
    { title: "Sales", link: "/sales" },
    { title: "Marketing", link: "/marketing" },
    { title: "Data", link: "/data-analytics" },
  ],
  [
    { title: "By Use Case" },
    { title: "Lead scoring", link: "/ai-lead-scoring-for-hubspot" },
    { title: "Sales metrics", link: "/metric-and-root-case-analysis" },
    { title: "Forecasting", link: "/forecasting" },
    { title: "Churn analysis", link: "/churn-analysis/" },
  ]
]

const customersLinks = [
  [
    { title: "Bruntwood Case Study", link: "/bruntwood-case-study" },
    { title: "Secret Escapes", link: "/secret-escapes" },
  ],
]

const resourcesLinks = [
  [
    //{ title: "Docs", link: "https://docs.getinfer.io/", external: true },
    { title: "Blog", link: "/blog" },
  ],
]

const companyLinks = [
  [
    { title: "About Infer", link: "/about-infer" },
    //{ title: "Contact Us", link: "/contact-us" },
  ],
]

const HeaderDropdown = ({
  id,
  title,
  links,
  colorClass,
  highlight,
  titleClass,
  linkTo,
  isRootPath,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const linkRef = React.useRef(null)
  const isOpen = !!anchorEl

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleTriggerMouseDown = event => {
    if (anchorEl === event.currentTarget) {
      return
    }
    setAnchorEl(event.currentTarget)
  }

  return (
    <button
      type="button"
      className="group relative block"
      onMouseEnter={handleTriggerMouseDown}
      onMouseLeave={handleClose}
    >
      <Link
        aria-label={`Learn about ${title}`}
        ref={linkRef}
        id={`${id}-nav-menu-button`}
        className={cx(headerItemClass, titleClass)}
        to={linkTo}
      >
        {title}
      </Link>
      <Menu
        id={`${id}-nav-menu`}
        aria-labelledby={`${id}-nav-menu-button`}
        anchorEl={anchorEl}
        open={isOpen}
        slots={{ root: Popper }}
        slotProps={{ root: { placement: "bottom-start" } }}
        className="z-10 pt-6"
      >
        <div
          className={cx(
            [title === "Resources"] ? "-left-[36px]" : "-left-[56px]",
            "relative -top-[12px] flex bg-black rounded-t-none rounded-header items-start space-x-10 px-14 pb-6 pt-6"
          )}
        >
          {links.map((item, index) => (
            <div key={`link-${id}-${index + 1}`}>
              {item.map((cell, idx) =>
                cell.link ? (
                  cell.external ? (
                    <a
                      aria-label={`Learn about ${cell.title}`}
                      key={`cell-${id}-${idx + 1}`}
                      className={cx(
                        "text-white text-base block py-3",
                        cell.hidden ? 'opacity-40 cursor-default' : colorClass,
                      )}
                      href={cell.link}
                    >
                      {cell.title}
                    </a>
                  ) : (
                    <Link
                      aria-label={`Learn about ${cell.title}`}
                      key={`cell-${id}-${idx + 1}`}
                      className={cx(
                        "text-white text-base block py-3",
                        cell.hidden ? 'opacity-40 cursor-default' : colorClass,
                      )}
                      to={cell.link}
                    >
                      {cell.title}
                    </Link>
                  )
                ) : (
                  <span
                    key={`cell-${id}-${idx + 1}`}
                    className={cx("text-base block py-2 leading-5", highlight)}
                  >
                    {cell.title}
                  </span>
                )
              )}
            </div>
          ))}
        </div>
      </Menu>
    </button>
  )
}

const headerItemClass = "text-white text-base group relative"

const MobileSection = ({
  id,
  title,
  links,
  colorClass,
  titleClass,
  linkTo,
}) => {
  const [isOpen, setIsOpen] = React.useState(title === "Platform")

  const toggleOpen = () => {
    setIsOpen(!isOpen)
  }

  const firstBatch = links.slice(0, 2)
  const secondBatch = links.slice(2, 4)

  return (
    <div className="border-b border-white pl-6">
      <div className="flex items-center justify-between h-12">
        <Link
          className={cx(headerItemClass, titleClass)}
          to={linkTo}
          aria-label={`Learn about ${title}`}
        >
          {title}
        </Link>
        <button
          type="button"
          className={cx(
            isOpen ? "rotate-90" : "-rotate-90",
            "mr-4 w-8 h-8 text-white"
          )}
          aria-label="Open menu"
          onClick={toggleOpen}
        >
          <ChevronIcon />
        </button>
      </div>
      <ul
        className={cx(
          isOpen ? "max-h-[400px]" : "max-h-0",
          "overflow-hidden transition-all"
        )}
      >
        <div
          className={cx(
            !!secondBatch.length && "grid grid-cols-2",
            "bg-black space-x-3 pb-4"
          )}
        >
          {[firstBatch, secondBatch].map((batch, index) => (
            <div className={`batch-${id}-${index}`}>
              {batch.map((item, index) => (
                <React.Fragment key={`link-${id}-${index + 1}`}>
                  {item.map((cell, idx) =>
                    cell.link ? (
                      cell.external ? (
                        <a
                          aria-label={`Learn about ${cell.title}`}
                          key={`cell-${id}-${idx + 1}`}
                          className={cx(
                            "text-base font-light block py-2 leading-5 pr-6 text-grey-200",
                            cell.hidden ? 'opacity-40 cursor-default' : colorClass,
                          )}
                          href={cell.link}
                        >
                          {cell.title}
                        </a>
                      ) : (
                        <Link
                          aria-label={`Learn about ${cell.title}`}
                          key={`cell-${id}-${idx + 1}`}
                          className={cx(
                            "text-base font-light block py-2 leading-5 pr-6 text-grey-200",
                            cell.hidden ? 'opacity-40 cursor-default' : colorClass,
                          )}
                          to={cell.link}
                        >
                          {cell.title}
                        </Link>
                      )
                    ) : (
                      <span
                        key={`cell-${id}-${idx + 1}`}
                        className="first:mt-0 mt-6 text-[14px] font-light block py-2 leading-5 text-white"
                      >
                        {cell.title}
                      </span>
                    )
                  )}
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </ul>
    </div>
  )
}

const Header = ({ isRootPath }) => {
  const { useState, useEffect } = React
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    isOpen
      ? document.body.classList.add("cut-body")
      : document.body.classList.remove("cut-body")
  }, [isOpen])

  const toggleMenu = () => {
    setIsOpen(!isOpen)
  }

  return (
    <>
      <div className="desktop:hidden">
        {isOpen ? (
          <header className="fixed top-12 left-0 w-full z-10 h-screen overflow-y-auto max-h-screen bg-black text-white">
            <div className="h-16 pl-6 flex items-center justify-between border-b border-white sticky top-0 bg-black z-10">
              <Link
                className="text-white"
                to="/"
                aria-label="Go to Platform page"
              >
                <InferLogo alt="Infer logo" height={17} width={76} />
              </Link>
              <button
                aria-label="Close menu"
                type="button"
                onClick={toggleMenu}
                className="w-10 h-8 pr-6 flex items-center justify-end"
              >
                <CloseIcon alt="Close icon" />
              </button>
            </div>
            <MobileSection
              id="platform"
              title="Platform"
              linkTo="/"
              links={platformLinks}
              titleClass="hover:text-yellow-300"
              colorClass="hover:text-yellow-100"
            />
            <MobileSection
              id="solutions"
              title="Solutions"
              linkTo="/"
              links={solutionsLinks}
              titleClass="hover:text-purple-300"
              colorClass="hover:text-purple-100"
            />
            <MobileSection
              id="customers"
              title="Customers"
              linkTo="/bruntwood-case-study"
              links={customersLinks}
              titleClass="hover:text-green-300"
              colorClass="hover:text-green-100"
            />
            <MobileSection
              id="resources"
              title="Resources"
              linkTo="/blog"
              links={resourcesLinks}
              titleClass="hover:text-red-300"
              colorClass="hover:text-red-100"
            />
            <MobileSection
              id="company"
              title="Company"
              linkTo="/about-infer"
              links={companyLinks}
              titleClass="hover:text-blue-300"
              colorClass="hover:text-blue-200"
            />
          </header>
        ) : (
          <div className="w-full px-3 pt-2.5 top-12 absolute z-[2]">
            <header className="px-3 h-10 w-full bg-black text-white rounded-button flex items-center justify-between">
              <Link
                className="text-white"
                to="/"
                aria-label="Go to Platform page"
              >
                <InferLogo alt="Infer logo" height={17} width={76} />
              </Link>
              <button
                aria-label="Open menu"
                type="button"
                onClick={toggleMenu}
                className="w-8 h-8 flex items-center justify-end"
              >
                <BurgerIcon alt="Open menu icon" />
              </button>
            </header>
          </div>
        )}
      </div>

      <div className="hidden desktop:block w-full px-6 top-12 absolute z-[2]">
        <header className="h-20 w-full bg-black text-white rounded-header">
          <div className="w-full container flex items-center justify-between h-full">
            <div className="flex items-center space-x-14">
              <HeaderDropdown
                isRootPath={isRootPath}
                id="platform"
                title="Platform"
                linkTo="/"
                links={platformLinks}
                titleClass="hover:text-yellow-300"
                colorClass="hover:text-yellow-100"
                highlight="text-yellow-100"
              />
              <HeaderDropdown
                isRootPath={isRootPath}
                id="solutions"
                title="Solutions"
                linkTo="/"
                links={solutionsLinks}
                titleClass="hover:text-purple-300"
                colorClass="hover:text-purple-100"
                highlight="text-purple-100"
              />
              <HeaderDropdown
                isRootPath={isRootPath}
                id="customers"
                title="Customers"
                linkTo="/bruntwood-case-study"
                links={customersLinks}
                titleClass="hover:text-green-300"
                colorClass="hover:text-green-100"
                highlight="text-green-100"
              />
              <HeaderDropdown
                isRootPath={isRootPath}
                id="resources"
                title="Resources"
                linkTo="/blog"
                links={resourcesLinks}
                titleClass="hover:text-red-300"
                colorClass="hover:text-red-100"
                highlight="text-red-100"
              />
              <HeaderDropdown
                isRootPath={isRootPath}
                id="company"
                title="Company"
                linkTo="/about-infer"
                links={companyLinks}
                titleClass="hover:text-blue-300"
                colorClass="hover:text-blue-200"
                highlight="text-blue-200"
              />
            </div>
            <Link
              className="text-white"
              to="/"
              aria-label="Go to Platform page"
            >
              <InferLogo alt="Infer logo" />
            </Link>
          </div>
        </header>
      </div>
    </>
  )
}

export default Header
